/**
 * Minimal ABIs for the REAL on-chain partial LAND sale (BUG3).
 *
 * BYTE-FAITHFUL to the frozen `TheGridSubdivisionVault` (deposit → split) and the
 * ERC-721 parent-approval surface. The seller signs REAL transactions with their
 * own wallet: setApprovalForAll(vault) → deposit(parentTokenId) → split(...).
 * The backend is the authority: it re-derives the split plan, reconciles the
 * child geometry from the on-chain vault view, and re-validates every listing.
 */

/** Rect struct = TheGridSubdivisionVault.Rect (uint16 x,y,w,h). */
const RECT_COMPONENTS = [
  { name: "x", type: "uint16" },
  { name: "y", type: "uint16" },
  { name: "w", type: "uint16" },
  { name: "h", type: "uint16" },
] as const;

export const SUBDIVISION_VAULT_ABI = [
  {
    type: "function",
    name: "deposit",
    stateMutability: "nonpayable",
    inputs: [{ name: "parentTokenId", type: "uint256" }],
    outputs: [{ name: "rootChildId", type: "uint256" }],
  },
  {
    type: "function",
    name: "split",
    stateMutability: "nonpayable",
    inputs: [
      { name: "childId", type: "uint256" },
      { name: "owners", type: "address[]" },
      { name: "rectsPerChild", type: "tuple[][]", components: RECT_COMPONENTS },
    ],
    outputs: [{ name: "newChildIds", type: "uint256[]" }],
  },
  {
    type: "function",
    name: "activeChildCount",
    stateMutability: "view",
    inputs: [{ name: "parentTokenId", type: "uint256" }],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    type: "function",
    name: "childActive",
    stateMutability: "view",
    inputs: [{ name: "childId", type: "uint256" }],
    outputs: [{ name: "", type: "bool" }],
  },
  {
    type: "event",
    name: "ParentDeposited",
    inputs: [
      { name: "parentTokenId", type: "uint256", indexed: true },
      { name: "depositor", type: "address", indexed: true },
      { name: "rootChildId", type: "uint256", indexed: false },
    ],
  },
  {
    type: "event",
    name: "ChildSplit",
    inputs: [
      { name: "parentTokenId", type: "uint256", indexed: true },
      { name: "oldChildId", type: "uint256", indexed: true },
      { name: "newChildIds", type: "uint256[]", indexed: false },
    ],
  },
] as const;

/** ERC-721 parent-collection approval surface (deposit pulls the parent NFT). */
export const ERC721_APPROVAL_ABI = [
  {
    type: "function",
    name: "isApprovedForAll",
    stateMutability: "view",
    inputs: [
      { name: "owner", type: "address" },
      { name: "operator", type: "address" },
    ],
    outputs: [{ name: "", type: "bool" }],
  },
  {
    type: "function",
    name: "setApprovalForAll",
    stateMutability: "nonpayable",
    inputs: [
      { name: "operator", type: "address" },
      { name: "approved", type: "bool" },
    ],
    outputs: [],
  },
  {
    type: "function",
    name: "getApproved",
    stateMutability: "view",
    inputs: [{ name: "tokenId", type: "uint256" }],
    outputs: [{ name: "", type: "address" }],
  },
  {
    type: "function",
    name: "approve",
    stateMutability: "nonpayable",
    inputs: [
      { name: "to", type: "address" },
      { name: "tokenId", type: "uint256" },
    ],
    outputs: [],
  },
] as const;
